import { useLocation } from "@solidjs/router";

import {
  type JSX,
  type ParentProps,
  createEffect,
  createSignal,
} from "solid-js";

import styles from "~/components/Modal.module.scss";
import rootStyles from "~/components/Root.module.scss";

interface ModalProps extends ParentProps {
  readonly id: string;
  readonly class?: string;
  readonly classList?: Record<string, boolean | undefined>;
  readonly title?: JSX.Element;
  readonly cropContent?: boolean;
  readonly onShow?: () => void;
  readonly onHide?: () => void;
}

export default function Modal(props: ModalProps) {
  const location = useLocation();
  const [dialogRef, setDialogRef] = createSignal<HTMLDialogElement>();
  const isDialogShowing = () => location.hash === `#${props.id}`;

  const closeModal: JSX.EventHandlerUnion<HTMLElement, Event> = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isDialogShowing()) {
      history.back();
    }
  };

  const maybeCloseModal: JSX.EventHandlerUnion<HTMLElement, MouseEvent> = (
    event,
  ) => {
    const dialog = dialogRef();
    if (!dialog) {
      return;
    }

    const rect = dialog.getBoundingClientRect();
    const isInDialog =
      rect.top <= event.clientY &&
      event.clientY <= rect.top + rect.height &&
      rect.left <= event.clientX &&
      event.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      closeModal(event);
    }
  };

  createEffect(() => {
    const dialog = dialogRef();
    if (!dialog) {
      return;
    }

    if (isDialogShowing()) {
      dialog.showModal();
      props.onShow?.();
      return;
    }

    dialog.close();
    props.onHide?.();
  });

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
    <dialog
      id={props.id}
      classList={{
        [props.class ?? ""]: true,
        ...props.classList,
        [styles.modal]: true,
      }}
      ref={setDialogRef}
      onClick={maybeCloseModal}
      onClose={closeModal}
    >
      <div class={styles["title-wrapper"]}>
        <h2>{props.title}</h2>
        <button class={styles.close} type="button" onClick={closeModal}>
          <IconTablerX />
          <span class={rootStyles["sr-only"]}>Fechar</span>
        </button>
      </div>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: this only stops propagation */}
      <div
        classList={{
          [styles.body]: true,
          [styles["crop-content"]]: props.cropContent,
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {props.children}
      </div>
    </dialog>
  );
}
